import { useState } from 'react';
import { FileCheck2, PlusCircle, RotateCcw, Trash2 } from 'lucide-react';

import ReusableModalWrapper from '../Modals/Common/ReusableModalWrapper';
import ActionButton from '../Buttons/ActionButton';
import Checkbox from '../Forms/Input/Checkbox';
import InputField from '../Forms/Input/InputField';
import ReturnDocumentSelector from './ReturnDocumentSelector';
import FiscalEventStatusBadge from './FiscalEventStatusBadge';
import FiscalEventDownloads from './FiscalEventDownloads';

import useReturnEvent from '../../hooks/fiscal/useReturnEvent';
import { validateReturnQuantity } from '../../features/fiscalReturn/returnValidation';

interface Props {
  isOpen: boolean;
  initialSaleId?: number | null;
  onClose: () => void;
  onCreated?: () => void;
  /** Renderiza el contenido sin el modal flotante (usado como cuerpo de página). */
  embedded?: boolean;
}

const typeLabel = (type?: string) =>
  ({ '01': 'Factura', '11': 'Factura de exportación', '14': 'Factura de sujeto excluido' } as Record<string, string>)[type || ''] || type || '—';

const dateLabel = (date?: string) => {
  if (!date) return '—';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : new Intl.DateTimeFormat('es-SV', { dateStyle: 'medium' }).format(parsed);
};

const money = (value?: number) => new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const ReturnEventWizard = ({ isOpen, initialSaleId, onClose, onCreated, embedded = false }: Props) => {
  const {
    documents,
    selectedLines,
    quantities,
    unitPrices,
    discounts,

    preview,
    previewLoading,
    result,

    busy,

    request,
    validationErrors,

    returnLineKey,

    handlerAddDocument,
    handlerRemoveDocument,

    handlerChangeSelection,
    handlerChangeQuantity,
    handlerChangeUnitPrice,
    handlerChangeDiscount,
    handlerSetAll,

    handlerSubmit,
    handlerResetReturnEvent,
  } = useReturnEvent({ isOpen, initialSaleId, onCreated });

  const [showAddDocument, setShowAddDocument] = useState(false);

  const handleClose = () => {
    if (busy) {
      const confirmed = window.confirm(
        'Hay una petición activa con Hacienda. ¿Desea cerrar y dejar de esperar la respuesta?'
      );
      if (!confirmed) return;
    }

    handlerResetReturnEvent();
    onClose();
  };

  const getLineError = (saleId: number, productId: number, availableQuantity: number) => {
    const key = returnLineKey(saleId, productId);
    if (!selectedLines[key]) return undefined;
    return validateReturnQuantity(quantities[key] || '', String(availableQuantity));
  };

  const canSubmit =
    !!request.lines.length && !validationErrors.length && !previewLoading && !!preview?.valid;

  const body = (
    <div className="space-y-5">

      {!result && (
        <>
          {(!initialSaleId || showAddDocument) && (
            <ReturnDocumentSelector
              selected={documents.flatMap((item) => (item.candidate ? [item.candidate] : []))}
              selectedSaleIds={documents.map((item) => item.eligibility.saleId)}
              customerId={documents[0]?.eligibility.customerId}
              documentType={documents[0]?.eligibility.documentType}
              onSelect={(candidate) => {
                void handlerAddDocument(candidate);
              }}
              disabled={busy}
            />
          )}

          {!!initialSaleId && !showAddDocument && documents.length > 0 && (
            <ActionButton
              label="Agregar otro documento"
              icon={<PlusCircle className="h-4 w-4" />}
              variant="secondary"
              size="sm"
              fullWidth={false}
              disabled={busy}
              onClick={() => setShowAddDocument(true)}
            />
          )}

          {initialSaleId && busy && documents.length === 0 && (
            <div className="rounded-xl border border-stroke bg-gray-1 px-5 py-6 text-center dark:border-strokedark dark:bg-meta-4">
              <p className="text-sm font-medium text-black dark:text-white">Consultando documento fiscal...</p>
              <p className="mt-1 text-xs text-body dark:text-bodydark">
                Validando si la venta puede participar en un Evento de Retorno.
              </p>
            </div>
          )}

          {documents.map(({ candidate, eligibility }) => (
            <section
              key={eligibility.saleId}
              className="overflow-hidden rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark"
            >
              <header className="bg-gray-1 flex flex-col gap-3 border-b border-stroke p-4 dark:border-strokedark dark:bg-meta-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileCheck2 className="h-5 w-5 shrink-0 text-primary" />
                    <h3 className="truncate font-semibold text-black dark:text-white">
                      {candidate?.documentNumber || 'Documento fiscal seleccionado'}
                    </h3>
                  </div>
                  <p
                    className="mt-1 max-w-3xl truncate text-xs text-body dark:text-bodydark"
                    title={candidate?.controlNumber || eligibility.generationCode}
                  >
                    {candidate?.controlNumber || eligibility.generationCode || 'Código fiscal disponible al consultar el detalle'}
                  </p>
                </div>

                <ActionButton
                  label="Quitar"
                  icon={<Trash2 className="h-4 w-4" />}
                  variant="danger"
                  size="sm"
                  fullWidth={false}
                  disabled={busy}
                  onClick={() => handlerRemoveDocument(eligibility.saleId)}
                />
              </header>

              <div className="p-4">
                <dl className="mb-5 grid gap-4 rounded-xl bg-primary/5 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Tipo DTE</dt>
                    <dd className="mt-1 font-semibold text-black dark:text-white">{typeLabel(eligibility.documentType)}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Sello de recepción</dt>
                    <dd className="mt-1 truncate font-medium text-black dark:text-white" title={eligibility.receptionSeal}>
                      {eligibility.receptionSeal || '—'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Fecha límite</dt>
                    <dd className="mt-1 font-medium text-black dark:text-white">{dateLabel(eligibility.deadline)}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium text-body dark:text-bodydark">Código de generación</dt>
                    <dd className="mt-1 truncate font-medium text-black dark:text-white" title={eligibility.generationCode}>
                      {eligibility.generationCode || '—'}
                    </dd>
                  </div>
                </dl>

                <div className="mb-4 flex flex-wrap gap-2">
                  <ActionButton
                    label="Retornar todo"
                    icon={<RotateCcw className="h-4 w-4" />}
                    size="sm"
                    fullWidth={false}
                    disabled={busy}
                    onClick={() => handlerSetAll(eligibility, true)}
                  />
                  <ActionButton
                    label="Limpiar selección"
                    variant="secondary"
                    size="sm"
                    fullWidth={false}
                    disabled={busy}
                    onClick={() => handlerSetAll(eligibility, false)}
                  />
                </div>

                {/* MOBILE */}
                <div className="grid gap-3 lg:hidden">
                  {eligibility.lines.map((line) => {
                    const key = returnLineKey(eligibility.saleId, line.productId);
                    const selected = Boolean(selectedLines[key]);
                    const error = getLineError(eligibility.saleId, line.productId, line.availableQuantity);

                    return (
                      <article key={key} className="rounded-xl border border-stroke p-4 dark:border-strokedark">
                        <Checkbox
                          id={`select-mobile-${key}`}
                          label={`${line.productCode} — ${line.description}`}
                          checked={selected}
                          onChange={() =>
                            handlerChangeSelection(
                              eligibility.saleId,
                              line.productId,
                              !selected,
                              line.availableQuantity,
                              line.unitPrice,
                              line.discount
                            )
                          }
                        />

                        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-gray-1 p-3 text-xs dark:bg-meta-4">
                          <div><span className="block text-body">Vendida</span><strong className="text-black dark:text-white">{line.soldQuantity}</strong></div>
                          <div><span className="block text-body">Retornada</span><strong className="text-black dark:text-white">{line.processedReturnQuantity}</strong></div>
                          <div><span className="block text-body">Disponible</span><strong className="text-black dark:text-white">{line.availableQuantity}</strong></div>
                          <div><span className="block text-body">Monto</span><strong className="text-black dark:text-white">{money(line.availableNetAmount)}</strong></div>
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-3">
                          <InputField
                            label="Cantidad a retornar"
                            ariaLabel={`Cantidad ${line.description}`}
                            name={`quantity-mobile-${key}`}
                            type="number"
                            min={0}
                            max={line.availableQuantity}
                            step="0.00000001"
                            value={quantities[key] || ''}
                            disabled={!selected || busy}
                            error={error}
                            onChange={(event) => handlerChangeQuantity(eligibility.saleId, line.productId, event.target.value)}
                          />
                          <InputField
                            label="Precio unitario"
                            ariaLabel={`Precio unitario ${line.description}`}
                            name={`price-mobile-${key}`}
                            type="number"
                            min={0}
                            step="0.00000001"
                            value={unitPrices[key] ?? ''}
                            disabled={!selected || busy}
                            onChange={(event) => handlerChangeUnitPrice(eligibility.saleId, line.productId, event.target.value)}
                          />
                          <InputField
                            label="Descuento"
                            ariaLabel={`Descuento ${line.description}`}
                            name={`discount-mobile-${key}`}
                            type="number"
                            min={0}
                            step="0.00000001"
                            value={discounts[key] ?? ''}
                            disabled={!selected || busy}
                            onChange={(event) => handlerChangeDiscount(eligibility.saleId, line.productId, event.target.value)}
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* DESKTOP */}
                <div className="hidden overflow-x-auto lg:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stroke text-left dark:border-strokedark">
                        <th className="p-3">Producto</th>
                        <th className="p-3">Vendida</th>
                        <th className="p-3">Retornada</th>
                        <th className="p-3">Disponible</th>
                        <th className="min-w-[150px] p-3">Precio unitario</th>
                        <th className="min-w-[150px] p-3">Descuento</th>
                        <th className="p-3">Monto disponible</th>
                        <th className="min-w-[190px] p-3">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligibility.lines.map((line) => {
                        const key = returnLineKey(eligibility.saleId, line.productId);
                        const selected = Boolean(selectedLines[key]);
                        const error = getLineError(eligibility.saleId, line.productId, line.availableQuantity);

                        return (
                          <tr key={key} className="border-b border-stroke align-top dark:border-strokedark">
                            <td className="p-3">
                              <Checkbox
                                id={`select-desktop-${key}`}
                                label={`${line.productCode} — ${line.description}`}
                                checked={selected}
                                onChange={() =>
                                  handlerChangeSelection(
                                    eligibility.saleId,
                                    line.productId,
                                    !selected,
                                    line.availableQuantity,
                                    line.unitPrice,
                                    line.discount
                                  )
                                }
                              />
                            </td>
                            <td className="p-3">{line.soldQuantity}</td>
                            <td className="p-3">{line.processedReturnQuantity}</td>
                            <td className="p-3">{line.availableQuantity}</td>
                            <td className="p-3">
                              <InputField
                                label="Precio unitario"
                                ariaLabel={`Precio unitario ${line.description}`}
                                name={`price-${key}`}
                                type="number"
                                min={0}
                                step="0.00000001"
                                value={unitPrices[key] ?? ''}
                                disabled={!selected || busy}
                                onChange={(event) => handlerChangeUnitPrice(eligibility.saleId, line.productId, event.target.value)}
                              />
                            </td>
                            <td className="p-3">
                              <InputField
                                label="Descuento"
                                ariaLabel={`Descuento ${line.description}`}
                                name={`discount-${key}`}
                                type="number"
                                min={0}
                                step="0.00000001"
                                value={discounts[key] ?? ''}
                                disabled={!selected || busy}
                                onChange={(event) => handlerChangeDiscount(eligibility.saleId, line.productId, event.target.value)}
                              />
                            </td>
                            <td className="p-3">{money(line.availableNetAmount)}</td>
                            <td className="p-3">
                              <InputField
                                label="Cantidad a retornar"
                                ariaLabel={`Cantidad ${line.description}`}
                                name={`quantity-${key}`}
                                type="number"
                                min={0}
                                max={line.availableQuantity}
                                step="0.00000001"
                                value={quantities[key] || ''}
                                disabled={!selected || busy}
                                error={error}
                                onChange={(event) => handlerChangeQuantity(eligibility.saleId, line.productId, event.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ))}

          {documents.length > 0 && (
            <section className="rounded-xl border border-primary/30 bg-primary/5 p-5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-black dark:text-white">Resumen fiscal</h3>
                  <p className="mt-1 text-xs text-body dark:text-bodydark">
                    {previewLoading
                      ? 'Calculando totales...'
                      : 'Se actualiza automáticamente al editar cantidades, precios o descuentos.'}
                  </p>
                </div>

                {preview && (
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      preview.valid ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {preview.valid ? 'Selección válida' : 'Requiere revisión'}
                  </span>
                )}
              </div>

              {preview && (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                    <PreviewMetric label="Documentos" value={String(preview.documentCount)} />
                    <PreviewMetric label="Líneas" value={String(preview.lineCount)} />
                    <PreviewMetric label="Cantidad" value={String(preview.totalQuantity)} />
                    <PreviewMetric label="Monto neto" value={money(preview.totalNetAmount)} />
                    <PreviewMetric label="Total del evento" value={money(preview.totalEventAmount)} />
                    <PreviewMetric label="Total del documento" value={money(preview.totalDocumentAmount)} />
                  </div>

                  {!!preview.warnings?.length && (
                    <div className="mt-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
                      <p className="mb-2 text-sm font-semibold text-warning">Advertencias</p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black dark:text-white">
                        {preview.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {!!validationErrors.length && documents.length > 0 && (
            <div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
              <p className="text-sm font-medium text-danger">
                Existen productos con cantidades que deben ser revisadas antes de continuar.
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse justify-end gap-2 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row">
            <ActionButton label="Cancelar" variant="secondary" fullWidth={false} disabled={busy} onClick={handleClose} />
            <ActionButton
              label="Confirmar y generar"
              loadingLabel="Generando..."
              loading={busy}
              disabled={!canSubmit}
              fullWidth={false}
              onClick={() => {
                void handlerSubmit();
              }}
            />
          </div>
        </>
      )}

      {result && (
        <section className="space-y-5 rounded-xl border border-success/30 bg-success/5 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-body dark:text-bodydark">Resultado</p>
              <FiscalEventStatusBadge status={result.status} />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-black dark:text-white">Evento fiscal generado</h3>
            <p className="mt-2 text-sm leading-6 text-body dark:text-bodydark">
              {result.status === 'SIGNED' && !result.transmissionAvailable
                ? 'El Evento de Retorno fue generado, firmado y almacenado correctamente. La transmisión hacia Hacienda no se encuentra disponible.'
                : result.status === 'PROCESSED'
                  ? 'El Evento de Retorno fue generado y procesado correctamente por Hacienda.'
                  : 'El Evento de Retorno fue generado correctamente.'}
            </p>
          </div>

          {result.pdfAvailable && (
            <div className="border-t border-success/20 pt-4">
              <FiscalEventDownloads eventId={result.id} eventType={result.eventType} status={result.status} />
            </div>
          )}

          <div className="flex justify-end border-t border-success/20 pt-4">
            <ActionButton label="Cerrar" fullWidth={false} onClick={handleClose} />
          </div>
        </section>
      )}
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <ReusableModalWrapper isOpen={isOpen} onClose={handleClose} title="Crear evento de retorno" maxWidth="max-w-6xl" hideFooter>
      {body}
    </ReusableModalWrapper>
  );
};

interface PreviewMetricProps {
  label: string;
  value: string;
}

const PreviewMetric = ({ label, value }: PreviewMetricProps) => (
  <div className="rounded-lg bg-white px-4 py-3 dark:bg-boxdark">
    <p className="text-xs text-body dark:text-bodydark">{label}</p>
    <p className="mt-1 font-semibold text-black dark:text-white">{value}</p>
  </div>
);

export default ReturnEventWizard;
